// AGC: Tour Welcome Modal - Shows on first visit to Home page (v6.9.0)
// AGC: Features: Mascot greeting, tour selection, "Don't show again" option

import React, { useState } from 'react'
import { X, Play, Sparkles } from 'lucide-react'
import { usePlayerPreferences } from '../../providers/PlayerPreferencesProvider'
import { useTourGuide } from '../../providers/TourGuideProvider'
import { allTours, type TourId } from '../../config/tourSteps'

// AGC: Click sound effect
const playClickSound = () => {
  try {
    const audio = new Audio('/sounds/click.mp3')
    audio.volume = 0.5 // AGC: Set volume to 50%
    audio.play().catch((error) => {
      // AGC: Silently fail if audio can't play (user interaction required, etc.)
      console.debug('AGC: Could not play click sound:', error)
    })
  } catch (error) {
    // AGC: Silently fail if audio creation fails
    console.debug('AGC: Could not create audio for click sound:', error)
  }
}

interface TourWelcomeModalProps {
  isOpen: boolean
  onClose: () => void
  onStartSimulation?: () => void  // AGC: Callback to start simulation battle
}

// AGC: Pointer Rock Mascot - larger version for welcome screen
const WelcomeMascot: React.FC = () => (
  <div className="relative w-24 h-24 mx-auto mb-4">
    {/* AGC: Glow effect */}
    <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-purple-500 rounded-full opacity-40 blur-xl animate-pulse" />
    
    {/* AGC: Main mascot circle */}
    <div className="relative w-full h-full bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-2xl border-4 border-white/20 overflow-hidden">
      {/* AGC: Pointer Rock image as mascot */}
      <img 
        src={`${import.meta.env.BASE_URL || '/'}pointerrock.png`}
        alt="Pointer Rock"
        className="w-full h-full object-contain"
        style={{ 
          imageRendering: 'crisp-edges',
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
        }}
      />
    </div>
    
    {/* AGC: Floating sparkles */}
    <div className="absolute -top-2 -right-2 text-2xl animate-bounce" style={{ animationDelay: '0s' }}>✨</div>
    <div className="absolute -bottom-1 -left-2 text-xl animate-bounce" style={{ animationDelay: '0.3s' }}>⭐</div>
    <div className="absolute top-0 right-1/4 text-lg animate-bounce" style={{ animationDelay: '0.6s' }}>💫</div>
  </div>
)

// AGC: Tour card for selection
const TourCard: React.FC<{
  id: TourId
  title: string
  description: string
  icon: string
  estimatedTime: string
  onStart: () => void
}> = ({ title, description, icon, estimatedTime, onStart }) => (
  <button
    onClick={() => {
      playClickSound()
      onStart()
    }}
    className="w-full p-3 rounded-xl bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700 hover:border-cyan-500/50 transition-all group text-left"
  >
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center text-xl group-hover:scale-110 transition-transform">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-white text-sm group-hover:text-cyan-300 transition-colors">
          {title}
        </h4>
        <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">
          {description}
        </p>
      </div>
      <div className="text-xs text-slate-500 whitespace-nowrap">
        {estimatedTime}
      </div>
    </div>
  </button>
)

export const TourWelcomeModal: React.FC<TourWelcomeModalProps> = ({ isOpen, onClose, onStartSimulation }) => {
  const { preferences, updatePreferences } = usePlayerPreferences()
  const { startTour } = useTourGuide()
  const [dontShowAgain, setDontShowAgain] = useState(false)

  // AGC: Handle "Don't show again" checkbox
  const handleDontShowAgainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDontShowAgain(e.target.checked)
  }

  // AGC: Handle close - save preference if "Don't show again" is checked
  const handleClose = () => {
    if (dontShowAgain) {
      updatePreferences({ showTourWelcomeModal: false })
    }
    onClose()
  }

  // AGC: Start a specific tour
  const handleStartTour = (tourId: TourId) => {
    // AGC: Blockchain tour opens slides instead of regular tour
    if (tourId === 'blockchain') {
      if (dontShowAgain) {
        updatePreferences({ showTourWelcomeModal: false })
      }
      onClose()
      // AGC: Dispatch event to open blockchain tour slides (rendered at App level)
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('blockchain-tour:open'))
      }, 300)
      return
    }

    if (dontShowAgain) {
      updatePreferences({ showTourWelcomeModal: false })
    }
    onClose()
    // AGC: Small delay to allow modal to close before tour starts
    setTimeout(() => {
      startTour(tourId)
    }, 300)
  }

  // AGC: Start full tour (navigation first)
  const handleStartFullTour = () => {
    handleStartTour('navigation')
  }

  if (!isOpen) {
    return null
  }

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* AGC: Backdrop with blur */}
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={handleClose}
          />
          
          {/* AGC: Modal content */}
          <div className="relative w-full max-w-md bg-gradient-to-b from-slate-900 to-slate-950 border border-cyan-500/30 rounded-3xl shadow-2xl shadow-cyan-500/10 overflow-hidden animate-in fade-in zoom-in duration-300">
            {/* AGC: Decorative gradient top */}
            <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-cyan-500/10 via-purple-500/10 to-transparent pointer-events-none" />
            
            {/* AGC: Close button */}
            <button
              onClick={() => {
                playClickSound()
                handleClose()
              }}
              className="absolute top-4 right-4 p-2 rounded-full bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors z-10"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            
            {/* AGC: Header with mascot */}
            <div className="relative pt-8 pb-4 px-6 text-center">
              <WelcomeMascot />
              
              <h2 className="text-2xl font-bold text-white mb-2">
                Welcome to <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">Origami Battles!</span>
              </h2>
              
              <p className="text-slate-400 text-sm">
                Would you like a quick guided tour to learn how to play?
              </p>
            </div>
            
            {/* AGC: Quick start button */}
            <div className="px-6 pb-4 space-y-2">
              <button
                onClick={() => {
                  playClickSound()
                  handleStartFullTour()
                }}
                className="w-full py-3 px-6 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white font-bold text-base shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 transition-all flex items-center justify-center gap-2 group"
              >
                <Play className="w-5 h-5 group-hover:scale-110 transition-transform" />
                Start Interactive Tour
                <Sparkles className="w-4 h-4 opacity-70" />
              </button>
              
              {/* AGC: Start Simulation Battle button */}
              {onStartSimulation && (
                <button
                  onClick={() => {
                    playClickSound()
                    if (dontShowAgain) {
                      updatePreferences({ showTourWelcomeModal: false })
                    }
                    onClose()
                    setTimeout(() => {
                      onStartSimulation()
                    }, 300)
                  }}
                  className="w-full py-3 px-6 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white font-bold text-base shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 transition-all flex items-center justify-center gap-2 group"
                >
                  <Play className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  Start Simulation Battle
                  <Sparkles className="w-4 h-4 opacity-70" />
                </button>
              )}
            </div>
            
            {/* AGC: Tour selection */}
            <div className="px-6 pb-4">
              <p className="text-xs text-slate-500 mb-3 text-center">Or choose a specific tour:</p>
              
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1 custom-scrollbar">
                {allTours.map((tour) => (
                  <TourCard
                    key={tour.id}
                    id={tour.id}
                    title={tour.title}
                    description={tour.description}
                    icon={tour.icon}
                    estimatedTime={tour.estimatedTime}
                    onStart={() => handleStartTour(tour.id)}
                  />
                ))}
              </div>
            </div>
            
            {/* AGC: Footer with "Don't show again" option */}
            <div className="px-6 py-4 bg-slate-900/80 border-t border-slate-800">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={dontShowAgain}
                  onChange={handleDontShowAgainChange}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0 cursor-pointer"
                />
                <span className="text-sm text-slate-400 group-hover:text-slate-300 transition-colors">
                  Don't show me again
                </span>
              </label>
              <p className="text-xs text-slate-600 mt-1 ml-6">
                You can replay tours anytime from Settings
              </p>
            </div>
            
            {/* AGC: Decorative corner accents */}
            <div className="absolute top-0 left-0 w-16 h-16 border-l-2 border-t-2 border-cyan-500/30 rounded-tl-3xl pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-16 h-16 border-r-2 border-b-2 border-purple-500/30 rounded-br-3xl pointer-events-none" />
          </div>
        </div>
      )}

    </>
  )
}

export default TourWelcomeModal

